


import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, AlertCircle } from "lucide-react";
import life from '../../Assets/Images/rewamp/life style.svg';
import diet from '../../Assets/Images/rewamp/dietery.svg';
import stress from '../../Assets/Images/rewamp/stress.svg';
import activity from '../../Assets/Images/rewamp/activity.svg';

// ✅ Reusable Simple Components
const Button = ({ children, onClick, variant = "primary", className = "", disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`rounded-full px-6 py-2 font-medium transition-all duration-200 
      ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : variant === "outline"
          ? "border border-[#99B3C8] text-[#004276] hover:bg-[#F1F8FD]"
          : "text-white"
      } ${className}`}
    style={
      !disabled && variant !== "outline"
        ? { backgroundColor: '#00CFA5' }
        : {}
    }
  >
    {children}
  </button>
);

const Input = ({ placeholder, value, onChange, disabled, className }) => (
  <input
    type="text"
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    disabled={disabled}
    className={`border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-1 ${className || ''}`}
  />
);

const Collapsible = ({ title, icon, children, isOpen, onToggle }) => {
  return (
    <div className="rounded-lg border border-[#C9EEF8] bg-white mb-4 overflow-hidden">
      <div
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 cursor-pointer bg-[#F1F8FD]"
      >
        <div className="flex items-center gap-3">
          {icon}
          <span className="font-semibold" style={{ color: 'var(--color-brand-primary)' }}>
            {title}
          </span>
        </div>
        <ChevronDown
          className={`h-5 w-5 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
          style={{ color: 'var(--color-brand-primary)' }}
        />
      </div>
      {isOpen && <div className="space-y-6 px-6 pb-6">{children}</div>}
    </div>
  );
};

// ✅ Step 3 Component
export default function LifestylePreferences({ onNext, onPrevious, patientData = {} }) {
  const [stressLevel, setStressLevel] = useState(""); // ⭐ Changed default from "Low" to empty string
  const [selectedSleep, setSelectedSleep] = useState("");
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [selectedDiets, setSelectedDiets] = useState([]);
  const [otherDiet, setOtherDiet] = useState("");
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [otherAllergy, setOtherAllergy] = useState("");
  const [selectedWater, setSelectedWater] = useState("");
  const [expandedSection, setExpandedSection] = useState("stress");
  const [errors, setErrors] = useState({});
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  
  // ✅ Restore previously saved lifestyle data
  useEffect(() => {
    if (patientData) {
      if (patientData.stress_level) {
        setStressLevel(patientData.stress_level);
      }
      if (patientData.sleep_pattern) {
        setSelectedSleep(patientData.sleep_pattern);
      }
      if (patientData.exercise_type) {
        const activities = patientData.exercise_type.split(", ").filter(a => a.trim());
        setSelectedActivities(activities);
      }
      if (patientData.activity_level) {
        setSelectedJob(patientData.activity_level);
      }
      if (patientData.food_preference) {
        const diets = patientData.food_preference.split(", ").filter(d => d.trim());
        setSelectedDiets(diets);
      }
      if (patientData.allergies) {
        const allergies = patientData.allergies.split(", ").filter(a => a.trim());
        setSelectedAllergies(allergies);
      }
      if (patientData.water_intake) {
        setSelectedWater(patientData.water_intake);
      }
    }
  }, [patientData]);

  const activityOptions = [
    "Sedentary",
    "Home Workout",
    "Gym Training",
    "Weight Training",
    "Cardio Training",
    "Yoga",
    "Sports Practice",
    "Ground Training",
    "Mixed Training",
  ];

  const jobOptions = ["Desk Job", "Standing Job", "Physically Active Job"];
  const dietOptions = [
    "Vegetarian",
    "Non-Vegetarian",
    "Vegan",
    "Eggetarian",
    "Pescatarian",
    "Lacto Vegetarian",
    "Ovo Vegetarian",
    "High Protein",
     ];
  const allergyOptions = [
    "None",
    "Dairy",
    "Peanuts",
    "Gluten",
    "Soy",
    "Seafood",
    "Eggs",
  ];
  const waterOptions = ["1 to 2 Ltr", "2 to 3 Ltr", "More than 3 Ltr"];
  const sleepOptions = [
    "Less than 6 hours",
    "6 to 7 hours",
    "7 to 8 hours",
    "More than 8 hours",
    "Irregular",
  ];
  const stressOptions = ["Low", "Moderate", "High", "Very High"];

  const handleAddDiet = () => {
    if (otherDiet.trim()) {
      setSelectedDiets([...selectedDiets, otherDiet]);
      setOtherDiet("");
    }
  };

  const handleAddAllergy = () => {
    if (otherAllergy.trim() && !selectedAllergies.includes('None')) {
      const otherAllergiesCount = selectedAllergies.filter(a => allergyOptions.includes(a)).length;
      const customAllergiesCount = selectedAllergies.filter(a => !allergyOptions.includes(a)).length;
      
      if (otherAllergiesCount + customAllergiesCount >= 4) {
        setErrors({ 
          ...errors, 
          allergies: 'You can select a maximum of 4 allergies' 
        });
        return;
      }
      
      setSelectedAllergies([...selectedAllergies, otherAllergy.trim()]);
      setOtherAllergy('');
      setErrors({ ...errors, allergies: null });
    }
  };

  // ⭐ UPDATED: Added stress, sleep, and activities validation
  const validateForm = () => {
    const newErrors = {};

    // Validate stress level
    if (!stressLevel) {
      newErrors.stress = "Please select your stress level";
    }

    // Validate sleep pattern
    if (!selectedSleep) {
      newErrors.sleep = "Please select your sleep pattern";
    }

    // ⭐ NEW: Validate physical activities
    if (selectedActivities.length === 0) {
      newErrors.activities = "Please select at least one physical activity";
    }

    // Validate dietary preferences
    if (selectedDiets.length === 0) {
      newErrors.diets = "Please select at least one dietary preference";
    }

    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  // ⭐ UPDATED: Improved handleSubmit with better error handling
  const handleSubmit = () => {
    const { isValid, errors: validationErrors } = validateForm();
    setErrors(validationErrors);
    
    if (!isValid) {
      // Open the appropriate section based on which validation failed
      if (validationErrors.stress || validationErrors.sleep) {
        setExpandedSection("stress");
      } else if (validationErrors.activities) {
        setExpandedSection("activity");
      } else if (validationErrors.diets) {
        setExpandedSection("diet");
      }
      return;
    }

    const formData = {
      ...patientData,
      stress_level: stressLevel,
      sleep_pattern: selectedSleep,
      exercise_type: selectedActivities.join(", "),
      activity_level: selectedJob,
      food_preference: selectedDiets.join(", "),
      allergies: selectedAllergies.join(", "),
      water_intake: selectedWater,
    };

    onNext(formData);
  };

  const handlePrevious = () => {
    const formData = {
      stress_level: stressLevel,
      sleep_pattern: selectedSleep,
      exercise_type: selectedActivities.join(", "),
      activity_level: selectedJob,
      food_preference: selectedDiets.join(", "),
      allergies: selectedAllergies.join(", "),
      water_intake: selectedWater,
    };

    onPrevious(formData);
  };

  const buttonClass = 'rounded-full border-2 px-6 py-2 text-sm font-medium transition-all cursor-pointer';
  const unselectedButtonClass = 'border-[#004276] bg-white text-[#004276] hover:border-[#003360] hover:bg-[#F1F8FD]';

  return (
  <div 
    className="py-2 sm:py-3 px-2 sm:px-4"
    style={{
      backgroundColor: '#fff',
        backgroundImage: 'var(--section-bg-gradient)'
    }}
  >
    <div className="w-full max-w-full mx-auto">
      
      
    <div className="mb-5 sm:mb-6 flex items-center gap-3 sm:gap-4 px-2 sm:px-4">
  
  {/* ICON (replaced) */}
  <div className="rounded-xl bg-[#EAF3FA] p-2.5 flex items-center justify-center shrink-0">
    <img 
      src={life} 
      alt="Basic Info" 
     className="h-12 w-12 sm:h-14 sm:w-14 object-contain"

    />
  </div>

  {/* TEXT */}
 <div className="flex-1 min-w-0">
    <h2 
      className="text-lg sm:text-2xl font-bold break-words"
      style={{ color: "var(--color-brand-primary)" }}
    >
       Lifestyle & Dietary Preferences
    </h2>

    <p className="text-sm sm:text-base text-textGray mt-1 break-words">
       Let us know your daily activity, sleep, and food choices
    </p>
  </div>

</div>

      

      <div className="px-0 sm:px-2 lg:px-4 space-y-3 sm:space-y-4">
        
        {/* Stress & Sleep - WITH VALIDATION */}
        <Collapsible
                    title={
            <span style={{ color: "var(--color-brand-primary)" }}>
              Stress & Sleep
              <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
            </span>
          }
          icon={
            <img
              src={stress}   // 👈 your imported icon
              alt="Stress & Sleep"
              className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
            />
          }
          isOpen={expandedSection === "stress"}
          onToggle={() => setExpandedSection(expandedSection === "stress" ? null : "stress")}
        >
          <div className="space-y-6 sm:space-y-8">
            {/* STRESS LEVEL */}
            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                How would you describe your stress level?
                <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
                {stressOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setStressLevel(option);
                      setErrors({ ...errors, stress: null });
                    }}
                    className={`${buttonClass} ${
                      stressLevel === option
                        ? "text-white"
                        : unselectedButtonClass
                    } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base`}
                    style={
                      stressLevel === option
                        ? { background: 'var(--color-brand-primary)' }
                        : {}
                    }
                  >
                    {option}
                  </button>
                ))}
              </div>
              {/* ⭐ ADDED: Error message for stress level */}
              {errors.stress && (
                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>{errors.stress}</p>
                </div>
              )}
            </div>

            {/* SLEEP PATTERN */}
            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                How do you describe your sleep pattern?
                <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {sleepOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      setSelectedSleep(option);
                      setErrors({ ...errors, sleep: null });
                    }}
                    className={`${buttonClass} ${
                      selectedSleep === option
                        ? "text-white"
                        : unselectedButtonClass
                    } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base`}
                    style={
                      selectedSleep === option
                        ? { background:'var(--color-brand-primary)' }
                        : {}
                    }
                  >
                    {option}
                  </button>
                ))}
              </div>
              {errors.sleep && (
                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>{errors.sleep}</p>
                </div>
              )}
            </div>
          </div>
        </Collapsible>

        {/* Activity - NOW REQUIRED */}
        <Collapsible
          title={
  <span style={{ color: "var(--color-brand-primary)" }}>
    Physical Activity
    <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
  </span>
}
icon={
  <img
    src={activity}   // 👈 your imported icon
    alt="Physical Activity"
    className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
  />
}
          isOpen={expandedSection === "activity"}
          onToggle={() => setExpandedSection(expandedSection === "activity" ? null : "activity")}
        >
          <div className="space-y-6 sm:space-y-8">
            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                What type of Physical activities do you engage in?
                <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {activityOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedActivities((prev) =>
                        prev.includes(option)
                          ? prev.filter((a) => a !== option)
                          : [...prev, option]
                      );
                      setErrors({ ...errors, activities: null });
                    }}
                    className={`${buttonClass} ${
                      selectedActivities.includes(option)
                        ? "text-white"
                        : unselectedButtonClass
                    } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base`}
                    style={
                      selectedActivities.includes(option)
                        ? { background:'var(--color-brand-primary)' }
                        : {}
                    }
                  >
                    {option}
                  </button>
                ))}
              </div>
              {/* ⭐ NEW: Error message for activities */}
              {errors.activities && (
                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>{errors.activities}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                What is your daily activity level at work or home?
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {jobOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedJob(option);
                      setErrors({ ...errors, job: null });
                    }}
                    className={`${buttonClass} ${
                      selectedJob === option
                        ? "text-white"
                        : unselectedButtonClass
                    } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base`}
                    style={
                      selectedJob === option
                        ? { background:'var(--color-brand-primary)' }
                        : {}
                    }
                  >
                    {option}
                  </button>
                ))}
              </div>
              {errors.job && (
                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>{errors.job}</p>
                </div>
              )}
            </div>
          </div>
        </Collapsible>

        {/* Diet & Allergies */}
        <Collapsible
          title={
  <span style={{ color: "var(--color-brand-primary)" }}>
    Diet & Allergies
    <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
  </span>
}
icon={
  <img
    src={diet}   // 👈 your imported icon
    alt="Diet & Allergies"
    className="h-6 w-6 sm:h-8 sm:w-8 object-contain"
  />
}
          isOpen={expandedSection === "diet"}
          onToggle={() => setExpandedSection(expandedSection === "diet" ? null : "diet")}
        >
          <div className="space-y-6 sm:space-y-8">
            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                What are your Dietary preferences?
                <span style={{ color: '#FF6B6B' }} className="ml-1">*</span>
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-2">
                {dietOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setErrors({ ...errors, diets: null });

                      setSelectedDiets((prev) => {
                        if (prev.includes(option)) {
                          setShowLimitWarning(false);
                          return prev.filter((d) => d !== option);
                        } else if (prev.length < 4) {
                          setShowLimitWarning(false);
                          return [...prev, option];
                        } else {
                          setShowLimitWarning(true);
                          return prev;
                        }
                      });
                    }}
                    className={`${buttonClass} ${
                      selectedDiets.includes(option)
                        ? "text-white"
                        : unselectedButtonClass
                    } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base`}
                    style={
                      selectedDiets.includes(option)
                        ? { background:'var(--color-brand-primary)' }
                        : {}
                    }
                  >
                    {option}
                  </button>
                ))}

                {selectedDiets
                  .filter((d) => !dietOptions.includes(d))
                  .map((diet) => (
                    <button
                      key={diet}
                      onClick={() =>
                        setSelectedDiets((prev) => prev.filter((d) => d !== diet))
                      }
                      className="rounded-full border-2 text-white px-4 sm:px-6 py-2 text-sm font-medium flex items-center gap-2 hover:opacity-80"
                      style={{ background:'var(--color-brand-primary)' }}
                    >
                      {diet}
                      <span className="font-bold cursor-pointer">×</span>
                    </button>
                  ))}
              </div>

              {showLimitWarning && (
                <div className="flex items-center gap-2 p-3 rounded-lg mb-3" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>
                    You can select up to 4 diets only.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Enter here"
                  value={otherDiet}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(value)) {
                      setOtherDiet(value);
                      setErrors({ ...errors, allergies: '' });
                    } else {
                      setErrors({ ...errors, allergies: 'Special characters are not allowed' });
                    }
                  }}
                  className="text-sm sm:text-base"
                />
                <Button 
                  onClick={handleAddDiet} 
                  disabled={!otherDiet.trim()} 
                  className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold whitespace-nowrap"
                >
                  Add
                </Button>
              </div>
              {errors.diets && (
                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>{errors.diets}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                Do you have any food allergies?
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4">
                {allergyOptions.map((option) => {
                  const isNone = option === 'None';
                  const noneSelected = selectedAllergies.includes('None');
                  const otherAllergiesCount = selectedAllergies.filter(a => a !== 'None').length;
                  const isDisabled = (isNone && selectedAllergies.length > 0 && !noneSelected) || 
                                     (!isNone && noneSelected) ||
                                     (!isNone && !selectedAllergies.includes(option) && otherAllergiesCount >= 4);

                  return (
                    <button
                      key={option}
                      onClick={() => {
                        if (isDisabled) return;

                        setSelectedAllergies((prev) => {
                          if (isNone) {
                            return prev.includes(option) ? [] : [option];
                          } else {
                            const withoutNone = prev.filter(a => a !== 'None');
                            return prev.includes(option)
                              ? prev.filter((a) => a !== option)
                              : [...withoutNone, option];
                          }
                        });
                        setErrors({ ...errors, allergies: null });
                      }}
                      disabled={isDisabled}
                      className={`${buttonClass} ${
                        selectedAllergies.includes(option) ? "text-white" : unselectedButtonClass
                      } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base ${
                        isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      style={
                        selectedAllergies.includes(option)
                          ? { background: 'var(--color-brand-primary)' }
                          : {}
                      }
                    >
                      {option}
                    </button>
                  );
                })}
                {selectedAllergies.filter(a => !allergyOptions.includes(a)).map((allergy) => (
                  <button
                    key={allergy}
                    onClick={() =>
                      setSelectedAllergies((prev) => prev.filter((a) => a !== allergy))
                    }
                    className="rounded-full border-2 text-white px-4 sm:px-6 py-2 text-sm font-medium flex items-center gap-2 hover:opacity-80"
                    style={{ backgroundColor: 'var(--color-brand-primary)' }}
                  >
                    {allergy}
                    <span className="font-bold cursor-pointer">×</span>
                  </button>
                ))}
              </div>
              
              {!selectedAllergies.includes('None') && selectedAllergies.length > 0 && (
                <div className="text-xs sm:text-sm text-gray-600 mb-3">
                  {selectedAllergies.filter(a => allergyOptions.includes(a)).length} of 4 allergies selected
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Enter here"
                  value={otherAllergy}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(value)) {
                      setOtherAllergy(value);
                      setErrors({ ...errors, allergies: '' });
                    } else {
                      setErrors({ ...errors, allergies: 'Special characters are not allowed' });
                    }
                  }}
                  disabled={selectedAllergies.includes('None')}
                  className={`text-sm sm:text-base ${
                    selectedAllergies.includes('None') ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
                <Button
                  onClick={handleAddAllergy}
                  disabled={!otherAllergy.trim() || selectedAllergies.includes('None')}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold whitespace-nowrap"
                >
                  Add
                </Button>
              </div>
              {errors.allergies && (
                <div
                  className="flex items-center gap-2 mt-3 p-3 rounded-lg"
                  style={{ backgroundColor: '#FFE8E8' }}
                >
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>
                    {errors.allergies}
                  </p>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm sm:text-base font-semibold text-quantumPrimary mb-3 sm:mb-4">
                How much water do you drink daily?
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {waterOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedWater(option);
                      setErrors({ ...errors, water: null });
                    }}
                    className={`${buttonClass} ${
                      selectedWater === option
                        ? "text-white"
                        : unselectedButtonClass
                    } px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base`}
                    style={
                      selectedWater === option
                        ? { background:'var(--color-brand-primary)' }
                        : {}
                    }
                  >
                    {option}
                  </button>
                ))}
              </div>
              {errors.water && (
                <div className="flex items-center gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
                  <AlertCircle className="h-4 w-4" style={{ color: '#FF6B6B' }} />
                  <p style={{ color: '#FF6B6B', fontSize: '14px', fontWeight: '500' }}>{errors.water}</p>
                </div>
              )}
            </div>
          </div>
        </Collapsible>

        {/* Navigation */}
        {Object.keys(errors).filter(k => errors[k]).length > 0 && (
          <div className="flex items-center gap-2 mt-6 p-3 rounded-lg" style={{ backgroundColor: '#FFE8E8' }}>
            <AlertCircle className="h-4 w-4 flex-shrink-0" style={{ color: '#b91c1c' }} />
            <p style={{ color: '#b91c1c', fontSize: '14px', fontWeight: '500' }}>
              Please fill in all required fields before proceeding.
            </p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0 mt-4 sm:mt-6 pt-6 sm:pt-8 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handlePrevious}
            className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-semibold border-2 text-[#004276]"
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 inline-block mr-2" /> Previous
          </Button>
          <button
  type="button"
  onClick={handleSubmit}
  className="flex items-center justify-center gap-2 rounded-full px-8 sm:px-10 py-2.5 sm:py-3 font-semibold text-white transition shadow-lg text-sm sm:text-base"
  style={{ backgroundColor: 'var(--color-brand-primary)' }}
  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
>
  Next <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
</button>
        </div>
      </div>
    </div>
  </div>
);
}